/**
 * threadMentionsRepository — the mention inbox, read side (2026-09-17).
 *
 * Two stores, deliberately separate:
 *
 *   · `users/{uid}/thread_mentions/{anchorId}` — SERVER-WRITE-ONLY rows, one per
 *     (message, recipient), filed by functions/src/notifications/mentionInbox.ts.
 *     Nothing here writes them. They record what somebody ELSE said, so a client
 *     able to write them could manufacture a mention that never happened.
 *
 *   · `users/{uid}/chat_threads_meta/mentions` — the viewer's OWN read state, a
 *     high-water mark plus a capped map (see src/types/threadMention.ts). This
 *     is the only thing the browser writes, and "mark all read" is one write of
 *     one number however many mentions exist.
 *
 * WHY A RECORD AND NOT A QUERY: `firestore.rules` gates a `list` on the message
 * store behind `isGroupThreadParticipant(threadId)`, per thread. A
 * collection-group list is evaluated symbolically with that wildcard unbound, so
 * "every message that mentions me" is not a query anyone can be authorized to
 * run — which is the whole reason the rows exist.
 *
 * Both listeners are fail-soft in the same direction: an error hands back an
 * empty result and retries on the shared group-listener ladder, because a
 * mentions list that renders nothing is a bad hour and a mentions list that
 * throws takes the thread rail down with it.
 */
import { onSnapshot, orderBy, where } from 'firebase/firestore';
import {
  assertCurrentUid,
  collection,
  db,
  doc,
  getDoc,
  limit,
  query,
  setDoc,
} from './firestoreUtils';
import { GROUP_LISTENER_RETRY_DELAYS_MS } from './groupThreadMessagesRepository';
import {
  EMPTY_MENTION_READ_STATE,
  compareMentionsNewestFirst,
  parseMentionReadState,
  parseThreadMention,
  type MentionReadState,
  type ThreadMention,
} from '../types/threadMention';
import { MENTION_LIST_CAP } from '../services/threadMentions/mentionInboxModel';

const MENTIONS_COLLECTION = 'thread_mentions';
const META_COLLECTION = 'chat_threads_meta';
const READ_STATE_DOC = 'mentions';

export interface SubscribeThreadMentionsOptions {
  /**
   * People-only is the list's DEFAULT view, and it is the query rather than a
   * render-time filter for a reason: Prest0n addresses the user in nearly every
   * reply, so filtering a fixed newest-50 window in memory would routinely leave
   * two or three teammate mentions out of fifty rows. Rides the
   * (byPrest0n ASC, orderTs DESC) composite in firestore.indexes.json; the
   * unfiltered form rides the automatic single-field index.
   */
  peopleOnly?: boolean;
  limit?: number;
}

/**
 * Listen to this person's mention rows, newest first. Every snapshot is handed
 * on, the empty one included — absence is information here (it is what "you are
 * all caught up" is drawn from), not a reason to leave the last list on screen.
 */
export function subscribeThreadMentions(
  uid: string,
  onRows: (rows: ThreadMention[]) => void,
  opts: SubscribeThreadMentionsOptions = {},
): () => void {
  const id = String(uid || '').trim();
  if (!id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = GROUP_LISTENER_RETRY_DELAYS_MS[
      Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)
    ];
    retryIndex += 1;
    retryTimer = setTimeout(attach, delay);
  };

  const attach = (): void => {
    if (stopped) return;
    try {
      const constraints = [
        ...(opts.peopleOnly ? [where('byPrest0n', '==', false)] : []),
        orderBy('orderTs', 'desc'),
        limit(opts.limit ?? MENTION_LIST_CAP),
      ];
      const unsubscribe = onSnapshot(
        query(collection(db, 'users', id, MENTIONS_COLLECTION), ...constraints),
        (snap) => {
          retryIndex = 0;
          const rows: ThreadMention[] = [];
          for (const d of snap.docs) {
            const row = parseThreadMention(d.id, d.data() as Record<string, unknown>);
            if (row) rows.push(row);
          }
          // The query already orders; this only makes equal instants stable, so
          // two mentions filed in the same millisecond cannot swap places
          // between renders and make the list look like it is moving.
          rows.sort(compareMentionsNewestFirst);
          onRows(rows);
        },
        () => {
          try { unsubscribe(); } catch { /* already detached */ }
          detachInner = null;
          onRows([]);
          scheduleRetry();
        },
      );
      detachInner = unsubscribe;
    } catch {
      onRows([]);
      scheduleRetry();
    }
  };

  attach();
  return () => {
    stopped = true;
    if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
    if (detachInner) { try { detachInner(); } catch { /* already detached */ } detachInner = null; }
  };
}

/** Listen to the viewer's own read state. Absent ⇒ nothing read yet. */
export function subscribeMentionReadState(
  uid: string,
  onState: (state: MentionReadState) => void,
): () => void {
  const id = String(uid || '').trim();
  if (!id) return () => {};
  try {
    return onSnapshot(
      doc(db, 'users', id, META_COLLECTION, READ_STATE_DOC),
      (snap) => onState(
        snap.exists()
          ? parseMentionReadState(snap.data() as Record<string, unknown>)
          : EMPTY_MENTION_READ_STATE,
      ),
      () => onState(EMPTY_MENTION_READ_STATE),
    );
  } catch {
    onState(EMPTY_MENTION_READ_STATE);
    return () => {};
  }
}

/** One read of the stored state, for a caller that needs it before a write. */
export async function readMentionReadState(uid: string): Promise<MentionReadState> {
  const id = String(uid || '').trim();
  if (!id) return EMPTY_MENTION_READ_STATE;
  try {
    const snap = await getDoc(doc(db, 'users', id, META_COLLECTION, READ_STATE_DOC));
    return snap.exists()
      ? parseMentionReadState(snap.data() as Record<string, unknown>)
      : EMPTY_MENTION_READ_STATE;
  } catch {
    return EMPTY_MENTION_READ_STATE;
  }
}

/**
 * Persist the read state. NOT a merge: `read` is a map whose whole point is that
 * entries LEAVE it — on an eviction, and on "mark all read", which clears it
 * because every id it held is now below the mark. A merge would deep-merge the
 * map and resurrect every id the caller just dropped, so the doc would grow
 * without bound and "mark all read" would not stick.
 *
 * Best-effort: a failed write leaves a mention showing as unread, which is the
 * safe direction — the wrong one would be telling somebody they had read
 * something they had not.
 */
export async function writeMentionReadState(uid: string, state: MentionReadState): Promise<boolean> {
  const id = String(uid || '').trim();
  if (!id) return false;
  try {
    assertCurrentUid(id, 'writeMentionReadState');
    await setDoc(doc(db, 'users', id, META_COLLECTION, READ_STATE_DOC), {
      lastReadAtMs: state.lastReadAtMs,
      read: { ...state.read },
      updatedAt: Date.now(),
    });
    return true;
  } catch {
    return false;
  }
}
